import React, { ReactNode, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Icons, ToolButton } from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Estilos para scrollbar sutil
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    height: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }
`;

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  SaveButton?: ReactNode; // Nuevo prop para el botón de guardar
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  UndoRedo,
  Secondary,
  SaveButton, // Nuevo prop
  ...props
}: HeaderProps): ReactNode {
  const [activeTool, setActiveTool] = useState<string | null>(null);

  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <IconPresentationProvider
        size="large"
        IconContainer={ToolButton}
      >
        <NavBar
          isSticky={isSticky}
          {...props}
        >
          <div className="flex h-[53px] w-full items-center gap-2 overflow-hidden">
            {/* Zona IZQUIERDA: herramientas con scroll horizontal */}
            <div
              className="custom-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden"
              style={{
                flexWrap: 'nowrap',
              }}
            >
              {children}
              <div
                className="flex items-center gap-2"
                style={{ flexWrap: 'nowrap' }}
              >
                {menuOptions
                  .filter(
                    option =>
                      !option.title.includes('Settings') &&
                      !option.title.includes('Preferences') &&
                      !option.title.includes('About') &&
                      !option.title.includes('Help') &&
                      // Herramientas específicas a eliminar de accesos directos
                      !option.title.includes('Image Slice Sync') &&
                      !option.title.includes('Reference Lines') &&
                      !option.title.includes('References Lines') &&
                      !option.title.includes('Image Overlay') &&
                      !option.title.includes('Stack Scroll') &&
                      !option.title.includes('Cine') &&
                      !option.title.includes('Calibration') &&
                      !option.title.includes('Dicom Tag Browser') &&
                      !option.title.includes('DICOM Tag Browser') &&
                      !option.title.includes('Magnify Probe') &&
                      !option.title.includes('Ultrasound Directional') &&
                      !option.title.includes('Segment Label Display') &&
                      !option.title.includes('Acerca de') &&
                      !option.title.includes('Preferencias')
                    // Solo mantener herramientas esenciales como accesos directos
                  )
                  .map((option, index) => {
                    // Primero intentar obtener el icono directamente por nombre
                    let IconComponent = null;

                    if (option.icon) {
                      // Intentar obtener el ícono desde Icons
                      IconComponent = Icons[option.icon as keyof typeof Icons];

                      // Si no se encuentra, intentar con el mapeo por nombre
                      if (!IconComponent && Icons.ByName) {
                        IconComponent = Icons.ByName[option.icon];
                      }
                    }

                    return (
                      <ToolButton
                        key={`tool-${index}-${option.title}`}
                        id={`header-tool-${index}`}
                        tooltip={option.title}
                        onInteraction={() => {
                          setActiveTool(option.title);
                          option.onClick();
                        }}
                        className={classNames(
                          'h-10 w-10 flex-shrink-0 rounded border transition-all duration-200',
                          activeTool === option.title
                            ? 'border-blue-400 bg-blue-600 text-white shadow-lg' // Estado activo
                            : 'border-gray-600 text-white hover:border-blue-500 hover:bg-blue-600 hover:text-white active:bg-blue-700' // Estado normal
                        )}
                      >
                        {IconComponent ? (
                          <IconComponent className="h-5 w-5" />
                        ) : (
                          <Icons.GearSettings className="h-5 w-5" />
                        )}
                      </ToolButton>
                    );
                  })}
              </div>
            </div>

            {/* Zona DERECHA: botones fijos siempre visibles */}
            <div className="z-10 flex flex-shrink-0 items-center gap-2">
              {UndoRedo}
              {SaveButton && (
                <>
                  <div
                    className="border-r"
                    style={{
                      height: '24px',
                      borderColor: 'var(--theme-accent, #60a5fa)',
                      opacity: 0.5,
                    }}
                  ></div>
                  {SaveButton}
                </>
              )}
            </div>
          </div>
        </NavBar>
      </IconPresentationProvider>
    </>
  );
}

export default Header;
